import { LoaderFunction, redirect } from 'react-router-dom';
import { getClassData, joinClass, stopRecording } from './apis';
import { decodeEncryptedURL, gibberish, logErrorApi } from './utils/common';
import base64 from 'base-64';

export const loginLoader: LoaderFunction = async ({ params }) => {
  const { classParams = '' } = params;

  let data = classParams.indexOf(gibberish) == 0 ? classParams.substring(gibberish.length) : '';

  const specialChars: Record<string, string> = {
    _p_: '+',
    _s_: '/',
    _e_: '=',
    _a_: '&',
    _q_: '?',
    _h_: '#'
  };

  Object.keys(specialChars).map((d) => {
    const re = new RegExp(`\\${d}`, 'g');
    data = data.replace(re, specialChars[d]);
  });

  const decodedData = decodeEncryptedURL(data);
  const [roomId, referredBy] = decodedData.split('&');
  if (decodedData.length === 0) throw new Error('no room id');
  return { roomId, referredBy: referredBy?.split('=')[1] || '' };
};

export const feedbackLoader: LoaderFunction = async ({ params, request }) => {
  const { id } = params;
  const [, searchParams] = request.url.split('?');

  const user = new URLSearchParams(searchParams).get('user');
  if (!user) throw new Error('No userdata in feedback form');

  const userData = await JSON.parse(base64.decode(user));
  return { roomId: id, ...userData };
};

export const classLoader: LoaderFunction = async ({ params, request }) => {
  const { classParams = '' } = params;

  const [, searchParams] = request.url.split('?');
  const isGuest = new URLSearchParams(searchParams).get('isGuest');
  const isAdmin = new URLSearchParams(searchParams).get('isAdmin');
  const isRecorder = new URLSearchParams(searchParams).get('isRecorder');

  console.warn('connected with', classParams);

  const response = await joinClass(classParams, !!isGuest);

  let [, dataString] = classParams.split(gibberish);

  dataString = decodeEncryptedURL(dataString);
  if (dataString.length === 0) throw new Error('Invalid encrypted url');

  const [data, queryParams = ''] = dataString.split('?');
  const [roomId, userId] = data.split('_');
  const referredBy = queryParams.includes('referredBy')
    ? queryParams.split('&')[1]?.split('=')[1]
    : '';

  if (response.status === 'success') {
    if (localStorage.getItem('error-retry') === 'retry') {
      setTimeout(() => {
        localStorage.removeItem('error-retry');
      }, 5000);
    }

    if (response.data) {
      const { presenterId, userId, roomId, queryParams = '' } = response.data;
      const params = Object.fromEntries(new URLSearchParams(queryParams));
      return {
        success: true,
        isRecorder,
        ...params,
        ...response.data,
        ...(userId === presenterId && { name: 'Teacher' }),
        ...(userId === presenterId && response.message === 'ONGOING' && { isRecording: true }),
        roomId: parseInt(roomId as string),
        referredBy: referredBy,
        message: response.message
      };
    }

    if (response.message && ['SCHEDULED', 'WAITING', 'UPCOMING'].includes(response.message))
      return { wait: true, classParams };

    return { error: true, ...response };
  } else if (isAdmin) {
    const presenterId = '1';

    const botState = localStorage.getItem('bot-entry');

    if (isAdmin && isRecorder && botState === 'used') {
      localStorage.removeItem('bot-entry');
      redirect('/over');
    }

    localStorage.setItem('bot-entry', 'used');

    const params = Object.fromEntries(new URLSearchParams(queryParams));

    return {
      success: true,
      isAdmin,
      isRecorder,
      name: 'Admin',
      ...params,
      userId,
      presenterId,
      isGuest: false,
      roomId: parseInt(roomId as string),
      startTime: Date.now(),
      endTime: Date.now() + 3600000
    };
  } else {
    if (isRecorder)
      return stopRecording(parseInt(roomId as string))
        .catch((error) =>
          logErrorApi(
            parseInt(roomId as string),
            1,
            userId,
            'ERROR: STOP RECORDING API FAIL',
            error.message
          )
        )
        .finally(() => ({
          error: true,
          ...response
        }));

    return { error: true, ...response };
  }
};

export const WhiteBoardLoader: LoaderFunction = async ({ params, request }) => {
  const { classParams } = params;
  const [, searchParams] = request.url.split('?');
  const isGuest = new URLSearchParams(searchParams).get('isGuest');
  const isAdmin = new URLSearchParams(searchParams).get('isAdmin');
  const isRecorder = new URLSearchParams(searchParams).get('isRecorder');

  console.warn('connected with', classParams);

  const response = await joinClass(classParams as string, !!isGuest);

  if (response.status === 'success') {
    if (localStorage.getItem('error-retry') === 'retry') {
      setTimeout(() => {
        localStorage.removeItem('error-retry');
      }, 5000);
    }

    const { startTime, presenterId, userId, roomId, queryParams = '' } = response.data;
    if (
      presenterId !== userId &&
      startTime &&
      (startTime > Date.now() || response.message === 'SCHEDULED')
    ) {
      if (startTime < Date.now() + 600000)
        return { wait: true, classParams, startTime: response.data.startTime }; // class starts later than 10 mins
      return { future: true };
    }
    const params = Object.fromEntries(new URLSearchParams(queryParams));

    return {
      success: true,
      isRecorder,
      ...params,
      ...response.data,
      ...(userId === presenterId && { name: 'Teacher' }),
      ...(userId === presenterId && response.message === 'ONGOING' && { isRecording: true }),
      roomId: parseInt(roomId as string),
      message: response.message,
      isWhiteBoard: true
    };
  } else {
    const presenterId = '1';
    let [, dataString] = (classParams as string).split(gibberish);

    dataString = decodeEncryptedURL(dataString);
    if (dataString.length === 0) throw new Error('Invalid encrypted url');

    const [data, queryParams = ''] = dataString.split('?');
    const [roomId, userId] = data.split('_');

    if (!isAdmin) {
      if (!isRecorder) return { error: true, isWhiteBoard: true, message: response.message };

      return stopRecording(parseInt(roomId as string))
        .catch((error) =>
          logErrorApi(
            parseInt(roomId as string),
            1,
            userId,
            'ERROR: STOP RECORDING API FAIL',
            error.message
          )
        )
        .finally(() => ({
          error: true
        }));
    }

    const botState = localStorage.getItem('bot-entry');

    if (isAdmin && isRecorder && botState === 'used') {
      localStorage.removeItem('bot-entry');
      redirect('/over');
    }

    localStorage.setItem('bot-entry', 'used');

    const params = Object.fromEntries(new URLSearchParams(queryParams));

    return {
      success: true,
      isAdmin,
      isRecorder,
      name: 'Admin',
      ...params,
      userId,
      presenterId,
      isGuest: false,
      roomId: parseInt(roomId as string),
      endTime: Date.now() + 3600000,
      isWhiteBoard: true,
      message: response.message
    };
  }
};

export const manageClassLoader: LoaderFunction = async ({ params }) => {
  const { roomId } = params;

  const urlParams = new URLSearchParams({ roomId: roomId as string });

  const response = await getClassData(urlParams);

  if (response.status === 'success' && response.data) return response.data;
  throw new Error('Error in API');
};
